import { useState, useEffect, useCallback } from 'react';
import './DashboardPage.css';

function SupportPage() {
    const [tickets, setTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replies, setReplies] = useState([]);
    const [newReply, setNewReply] = useState('');
    const [isSendingReply, setIsSendingReply] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchTickets = useCallback(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (session && session.userId) {
            fetch(`${API_URL}/api/support/tickets/${session.userId}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success) {
                        const mappedTickets = data.tickets.map(t => ({
                            id: t.id,
                            subject: t.subject,
                            category: t.category,
                            description: t.description,
                            status: t.status,
                            priority: t.priority,
                            createdDate: new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
                            lastUpdate: new Date(t.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                        }));
                        setTickets(mappedTickets);
                    }
                })
                .catch(err => console.error('Error fetching tickets:', err));
        }
    }, [API_URL]);

    const fetchReplies = useCallback((ticketId) => {
        fetch(`${API_URL}/api/support/tickets/${ticketId}/replies`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setReplies(data.replies);
                }
            })
            .catch(err => console.error('Error fetching replies:', err));
    }, [API_URL]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const [showTicketModal, setShowTicketModal] = useState(false);

    const handleSubmitTicket = async (e) => {
        e.preventDefault();
        const session = JSON.parse(localStorage.getItem('userSession'));

        const form = e.target;
        const category = form.elements.category.value;
        const subject = form.elements.subject.value;
        const description = form.elements.description.value;

        try {
            const response = await fetch(`${API_URL}/api/support/create-ticket`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    category,
                    subject,
                    description
                })
            });
            const data = await response.json();
            if (data.success) {
                alert('Support ticket created successfully! Our team will contact you soon.');
                setShowTicketModal(false);
                fetchTickets();
            }
        } catch (err) {
            alert('Failed to create ticket. Please try again.');
        }
    };

    const handleTicketClick = (ticket) => {
        setSelectedTicket(ticket);
        fetchReplies(ticket.id);
    };

    const handleReplySubmit = async (e) => {
        e.preventDefault();
        if (!newReply.trim() || isSendingReply) return;

        const session = JSON.parse(localStorage.getItem('userSession'));

        try {
            setIsSendingReply(true);
            const response = await fetch(`${API_URL}/api/support/tickets/${selectedTicket.id}/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    message: newReply,
                    isAdminReply: false
                })
            });
            const data = await response.json();
            if (data.success) {
                setNewReply('');
                fetchReplies(selectedTicket.id);
            }
        } catch (err) {
            console.error('Reply error:', err);
        } finally {
            setIsSendingReply(false);
        }
    };

    return (
        <div className="dashboard-page-new">
            <div className="welcome-section">
                <div>
                    <h1>Support Helpdesk 💬</h1>
                    <p className="welcome-subtitle">Need help? We're just a message away.</p>
                </div>
                <button className="btn-primary" onClick={() => setShowTicketModal(true)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    New Support Request
                </button>
            </div>

            {/* Support Tickets List */}
            <div className="content-section" style={{ marginTop: '1rem' }}>
                <div className="section-header-new">
                    <h2>Your Tickets</h2>
                    <span className="loan-count">{tickets.length} active or past issues</span>
                </div>

                <div className="disbursement-table">
                    <div className="table-header">
                        <div className="table-col">Ticket ID</div>
                        <div className="table-col">Category</div>
                        <div className="table-col">Subject</div>
                        <div className="table-col">Last Updated</div>
                        <div className="table-col">Status</div>
                        <div className="table-col">Action</div>
                    </div>
                    {tickets.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                            You haven't raised any support tickets yet.
                        </div>
                    ) : (
                        tickets.map((ticket) => (
                            <div key={ticket.id} className="table-row">
                                <div className="table-cell">
                                    <span className="cell-label">ID</span>
                                    <span className="cell-value account-num">#TIC{ticket.id.toString().padStart(4, '0')}</span>
                                </div>
                                <div className="table-cell">
                                    <span className="cell-label">Category</span>
                                    <span className="cell-value" style={{ textTransform: 'capitalize' }}>{ticket.category}</span>
                                </div>
                                <div className="table-cell">
                                    <span className="cell-label">Subject</span>
                                    <span className="cell-value" style={{ fontWeight: '600' }}>{ticket.subject}</span>
                                </div>
                                <div className="table-cell">
                                    <span className="cell-label">Updated</span>
                                    <span className="cell-value">{ticket.lastUpdate}</span>
                                </div>
                                <div className="table-cell">
                                    <span className="cell-label">Status</span>
                                    <span className={`status-pill ${ticket.status === 'Open' ? 'pending' :
                                            ticket.status === 'In Progress' ? 'in-progress' :
                                                ticket.status === 'Resolved' ? 'completed' : 'closed'
                                        }`}>
                                        {ticket.status}
                                    </span>
                                </div>
                                <div className="table-cell">
                                    <span className="cell-label">Action</span>
                                    <button
                                        className="btn-secondary"
                                        style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                                        onClick={() => handleTicketClick(ticket)}
                                    >
                                        View & Reply
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Contact Options */}
            <div className="content-section" style={{ marginTop: '2rem' }}>
                <div className="section-header-new">
                    <h2>Other Ways to Connect</h2>
                </div>

                <div className="stats-cards">
                    <div className="stat-card-new" style={{ cursor: 'pointer' }}>
                        <div className="stat-icon" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📞</div>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>Call Us</h3>
                        <p style={{ margin: 0, color: '#666' }}>1800-XXX-XXXX</p>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#999' }}>Mon-Sat, 9 AM - 6 PM</p>
                    </div>

                    <div className="stat-card-new" style={{ cursor: 'pointer' }}>
                        <div className="stat-icon" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✉️</div>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>Email Us</h3>
                        <p style={{ margin: 0, color: '#666' }}>support@efin.co.in</p>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#999' }}>Response within 24h</p>
                    </div>

                    <div className="stat-card-new" style={{ cursor: 'pointer' }}>
                        <div className="stat-icon" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💬</div>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>Live Chat</h3>
                        <p style={{ margin: 0, color: '#666' }}>Chat with us</p>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#999' }}>Available now</p>
                    </div>

                    <div className="stat-card-new" style={{ cursor: 'pointer' }}>
                        <div className="stat-icon" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📱</div>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>WhatsApp</h3>
                        <p style={{ margin: 0, color: '#666' }}>+91 XXXXX XXXXX</p>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#999' }}>Fast response</p>
                    </div>
                </div>
            </div>

            {/* Conversation/Reply Modal */}
            {selectedTicket && (
                <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
                    <div className="loan-modal" style={{ maxWidth: '600px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setSelectedTicket(null)}>×</button>
                        <div className="modal-header">
                            <span className={`status-pill ${selectedTicket.status === 'Open' ? 'pending' :
                                selectedTicket.status === 'In Progress' ? 'in-progress' :
                                    selectedTicket.status === 'Resolved' ? 'completed' : 'closed'
                                }`} style={{ marginBottom: '0.5rem' }}>
                                {selectedTicket.status}
                            </span>
                            <h2>{selectedTicket.subject}</h2>
                            <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                                #TIC{selectedTicket.id.toString().padStart(4, '0')} • {selectedTicket.category.toUpperCase()}
                            </p>
                        </div>

                        <div className="chat-thread" style={{
                            maxHeight: '350px',
                            overflowY: 'auto',
                            padding: '1rem',
                            background: '#f8fafc',
                            borderRadius: '12px',
                            margin: '1.5rem 0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                        }}>
                            {/* Original Description */}
                            <div className="chat-message user" style={{
                                alignSelf: 'flex-start',
                                maxWidth: '85%',
                                padding: '12px 16px',
                                background: '#1a1a1a',
                                color: '#fff',
                                borderRadius: '14px 14px 14px 4px',
                                fontSize: '0.95rem'
                            }}>
                                <p style={{ margin: 0 }}>{selectedTicket.description}</p>
                                <span style={{ fontSize: '0.7rem', display: 'block', marginTop: '6px', opacity: 0.7 }}>
                                    {selectedTicket.createdDate} (Original Message)
                                </span>
                            </div>

                            {/* Replies */}
                            {replies.map(reply => (
                                <div key={reply.id} className={`chat-message ${reply.is_admin_reply ? 'admin' : 'user'}`} style={{
                                    alignSelf: reply.is_admin_reply ? 'flex-end' : 'flex-start',
                                    maxWidth: '85%',
                                    padding: '12px 16px',
                                    background: reply.is_admin_reply ? '#f7bd20' : '#1a1a1a',
                                    color: reply.is_admin_reply ? '#1a1a1a' : '#fff',
                                    borderRadius: reply.is_admin_reply ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                    fontSize: '0.95rem'
                                }}>
                                    <p style={{ margin: 0 }}>{reply.message}</p>
                                    <span style={{ fontSize: '0.7rem', display: 'block', marginTop: '6px', opacity: 0.7 }}>
                                        {reply.is_admin_reply ? 'Admin Support' : 'You'} • {new Date(reply.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {selectedTicket.status !== 'Closed' && selectedTicket.status !== 'Resolved' ? (
                            <form onSubmit={handleReplySubmit} style={{ display: 'flex', gap: '10px' }}>
                                <input
                                    type="text"
                                    value={newReply}
                                    onChange={(e) => setNewReply(e.target.value)}
                                    placeholder="Type your reply here..."
                                    style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid #e2e8f0', outline: 'none' }}
                                    disabled={isSendingReply}
                                />
                                <button type="submit" className="btn-primary" style={{ padding: '0 20px' }} disabled={isSendingReply}>
                                    {isSendingReply ? '...' : 'Send'}
                                </button>
                            </form>
                        ) : (
                            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9rem', fontStyle: 'italic' }}>
                                This ticket is {selectedTicket.status.toLowerCase()} and cannot be replied to.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Create Ticket Modal */}
            {showTicketModal && (
                <div className="modal-overlay" onClick={() => setShowTicketModal(false)}>
                    <div className="loan-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setShowTicketModal(false)}>×</button>
                        <h2>New Support Request</h2>
                        <p className="modal-subtitle">Briefly describe your issue and we'll help you out.</p>
                        <form onSubmit={handleSubmitTicket}>
                            <div className="form-group">
                                <label>What can we help you with? *</label>
                                <select name="category" required>
                                    <option value="">Select a category</option>
                                    <option value="loan">Loan Inquiry</option>
                                    <option value="payment">EMI & Payments</option>
                                    <option value="kyc">KYC & Documents</option>
                                    <option value="account">Account Access</option>
                                    <option value="other">General Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Subject *</label>
                                <input name="subject" type="text" required placeholder="Short summary (e.g., Cannot upload PAN card)" />
                            </div>
                            <div className="form-group">
                                <label>Description *</label>
                                <textarea name="description" rows="4" required placeholder="Detailed explanation..."></textarea>
                            </div>
                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowTicketModal(false)}>
                                    Discard
                                </button>
                                <button type="submit" className="btn-primary">
                                    Send Request
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SupportPage;
